import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Phone, PhoneCall, Calendar, Award, Activity, BarChart2, RefreshCw, ChevronRight,
  Bot, Clock, TrendingUp, Zap, PercentIcon,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import {
  ENGAGE_AI_CALLER, OUTCOME_BADGE, STATUS_BADGE,
  formatDuration, prettyOutcome, prettyStatus, LANGUAGE_LABEL,
} from './aiCallerConstants';
import { getEngageSourceBadge, ENGAGE_SOURCE_LABEL } from '../../../utils/engageStageBadge';


// ── Small building blocks ───────────────────────────────────────────────────

const StatCard = ({ label, value, icon: Icon, tone, sub, valueTone }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl p-3.5 border border-gray-100 dark:border-gray-700">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">{label}</p>
        <p className={cn('text-2xl font-bold mt-1', valueTone || 'text-gray-900 dark:text-white')}>{value ?? '—'}</p>
        {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
      </div>
      <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center', tone)}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);


function VolumeTrend({ data }) {
  const { path, max, points, ticks } = useMemo(() => {
    if (!data || data.length === 0) return { path: '', max: 0, points: [], ticks: [] };
    const W = 100, H = 40;
    const localMax = Math.max(1, ...data.map(d => d.total));
    const step = W / Math.max(1, data.length - 1);
    const pts = data.map((d, i) => ({
      x: i * step,
      y: H - (d.total / localMax) * H,
      day: d.date, total: d.total, demos: d.demos,
    }));
    const p = pts.map((pt, i) => (i ? 'L' : 'M') + pt.x.toFixed(2) + ' ' + pt.y.toFixed(2)).join(' ');
    return { path: p, max: localMax, points: pts, ticks: [data[0]?.date, data[Math.floor(data.length / 2)]?.date, data[data.length - 1]?.date] };
  }, [data]);

  const totalCalls = (data || []).reduce((s, d) => s + d.total, 0);
  const totalDemos = (data || []).reduce((s, d) => s + d.demos, 0);
  const areaPath = path ? path + ` L 100 40 L 0 40 Z` : '';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-teal-500" /> Call volume — last 14 days
          </h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {totalCalls} calls · {totalDemos} demos ({totalCalls ? Math.round(totalDemos / totalCalls * 100) : 0}% conv.)
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Peak day</div>
          <div className="text-sm font-bold text-gray-900 dark:text-white">{max}</div>
        </div>
      </div>
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-24">
        <defs>
          <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#14b8a6" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
          </linearGradient>
        </defs>
        {areaPath && <path d={areaPath} fill="url(#areaGradient)" />}
        {path && <path d={path} stroke="#14b8a6" strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke" />}
        {points.map((pt, i) => (
          <circle key={i} cx={pt.x} cy={pt.y} r="1.4" fill={pt.demos > 0 ? '#10b981' : '#14b8a6'}
            vectorEffect="non-scaling-stroke">
            <title>{`${pt.day}: ${pt.total} calls, ${pt.demos} demos`}</title>
          </circle>
        ))}
      </svg>
      <div className="flex items-center justify-between text-[10px] text-gray-400 mt-1">
        {ticks.filter(Boolean).map((d, i) => (
          <span key={i}>{new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
        ))}
      </div>
    </div>
  );
}


function HourlyDistribution({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map(d => d.total));
  const bestHour = [...data].sort((a, b) => b.conversion_rate - a.conversion_rate).find(d => d.total > 0);
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-purple-500" /> Best times to call · 30d
        </h3>
        {bestHour && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Best hour</div>
            <div className="text-sm font-bold text-purple-600 dark:text-purple-400">
              {String(bestHour.hour).padStart(2, '0')}:00 · {bestHour.conversion_rate}% conv
            </div>
          </div>
        )}
      </div>
      <div className="flex items-end gap-0.5 h-24">
        {data.map(d => {
          const h = d.total ? (d.total / max) * 100 : 0;
          const dh = d.demos ? (d.demos / max) * 100 : 0;
          return (
            <div key={d.hour} className="flex-1 flex flex-col items-center gap-0.5 group relative">
              <div className="w-full relative" style={{ height: '100%' }}>
                <div className="absolute bottom-0 w-full bg-teal-200 dark:bg-teal-900/40 rounded-t-sm" style={{ height: `${h}%` }} />
                <div className="absolute bottom-0 w-full bg-gradient-to-t from-green-500 to-emerald-400 rounded-t-sm" style={{ height: `${dh}%` }} />
              </div>
              {d.total > 0 && (
                <div className="hidden group-hover:block absolute -top-9 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-10">
                  {String(d.hour).padStart(2, '0')}:00 · {d.total} calls · {d.demos} demos
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-[10px] text-gray-400 mt-1 px-0.5">
        {[0, 6, 12, 18, 23].map(h => <span key={h}>{String(h).padStart(2, '0')}:00</span>)}
      </div>
      <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-teal-300 dark:bg-teal-900 rounded-sm" /> All calls</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-gradient-to-t from-green-500 to-emerald-400 rounded-sm" /> Demos booked</span>
      </div>
    </div>
  );
}


function Funnel({ funnel }) {
  if (!funnel) return null;
  const steps = [
    { key: 'attempted',   label: 'Attempted',       tone: 'from-slate-400 to-slate-500' },
    { key: 'answered',    label: 'Answered',        tone: 'from-blue-500 to-cyan-500' },
    { key: 'engaged',     label: 'Engaged (≥30s)',  tone: 'from-indigo-500 to-purple-500' },
    { key: 'qualified',   label: 'Qualified',       tone: 'from-purple-500 to-pink-500' },
    { key: 'demo_booked', label: 'Demo booked',     tone: 'from-green-500 to-emerald-600' },
  ];
  const top = funnel.attempted || 1;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
        <Zap className="w-4 h-4 text-amber-500" /> Call outcome funnel · 30d
      </h3>
      <div className="space-y-1.5">
        {steps.map((s, i) => {
          const v = funnel[s.key] || 0;
          const pct = Math.round(v / top * 100);
          const drop = i > 0 ? (funnel[steps[i - 1].key] || 0) - v : 0;
          return (
            <div key={s.key} className="flex items-center gap-2 text-sm">
              <span className="w-32 flex-shrink-0 text-gray-700 dark:text-gray-300 text-[12px]">{s.label}</span>
              <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden relative">
                <div className={cn('h-full bg-gradient-to-r rounded-full transition-all', s.tone)} style={{ width: `${pct}%` }} />
                <span className="absolute inset-0 flex items-center px-2.5 text-[11px] font-bold text-white mix-blend-difference">
                  {v} · {pct}%
                </span>
              </div>
              {i > 0 && drop > 0
                ? <span className="w-14 text-[10px] text-red-500 text-right">−{drop}</span>
                : <span className="w-14" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function SourceMatrix({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
          <BarChart2 className="w-4 h-4 text-orange-500" /> Which sources convert best
        </h3>
        <p className="text-sm text-gray-500 mt-4 text-center dark:text-gray-400">No source data yet.</p>
      </div>
    );
  }
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
          <BarChart2 className="w-4 h-4 text-orange-500" /> Which sources convert best · 30d
        </h3>
        <span className="text-[10px] text-gray-500">demo / total AI calls</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-900/40 text-[10px] uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Source</th>
            <th className="px-3 py-2 text-right font-medium">Called</th>
            <th className="px-3 py-2 text-right font-medium">Demos</th>
            <th className="px-3 py-2 text-right font-medium">No-answer</th>
            <th className="px-3 py-2 text-right font-medium">Conv.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map(r => (
            <tr key={r.source} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
              <td className="px-3 py-2">
                <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageSourceBadge(r.source))}>
                  {ENGAGE_SOURCE_LABEL[r.source] || r.source}
                </span>
              </td>
              <td className="px-3 py-2 text-right font-medium">{r.total}</td>
              <td className="px-3 py-2 text-right text-green-600 dark:text-green-400 font-medium">{r.demos}</td>
              <td className="px-3 py-2 text-right text-red-500">{r.no_answer}</td>
              <td className="px-3 py-2 text-right">
                <span className={cn(
                  'inline-block px-2 py-0.5 rounded-full text-[10px] font-medium',
                  r.conversion_rate >= 30 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                  r.conversion_rate >= 15 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                )}>
                  {r.conversion_rate}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function LanguageConversion({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
        <Activity className="w-4 h-4 text-teal-500" /> Conversion by language · 30d
      </h3>
      <div className="space-y-2">
        {rows.map(r => (
          <div key={r.language} className="flex items-center gap-2 text-sm">
            <span className="w-20 flex-shrink-0 text-gray-700 dark:text-gray-300 font-medium">
              {LANGUAGE_LABEL[r.language] || r.language}
            </span>
            <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden relative">
              <div className="h-full bg-gradient-to-r from-teal-400 to-cyan-500 rounded-full"
                   style={{ width: `${Math.min(100, r.conversion_rate)}%` }} />
              <span className="absolute inset-0 flex items-center justify-end pr-2 text-[10px] font-bold text-gray-700 dark:text-gray-200">
                {r.demos}/{r.total} · {r.conversion_rate}%
              </span>
            </div>
            <span className="text-[10px] text-gray-500 w-16 text-right">avg {formatDuration(r.avg_duration)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function AvgDurationByOutcome({ rows }) {
  if (!rows || rows.length === 0) return null;
  const max = Math.max(1, ...rows.map(r => r.avg_duration));
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
        <Clock className="w-4 h-4 text-indigo-500" /> Avg call duration by outcome · 30d
      </h3>
      <div className="space-y-1.5">
        {rows.map(r => (
          <div key={r.outcome} className="flex items-center gap-2 text-sm">
            <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium min-w-[110px] text-center flex-shrink-0',
              OUTCOME_BADGE[r.outcome] || 'bg-gray-100 text-gray-700')}>
              {prettyOutcome(r.outcome)}
            </span>
            <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full"
                   style={{ width: `${(r.avg_duration / max) * 100}%` }} />
            </div>
            <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 w-24 text-right">
              {formatDuration(r.avg_duration)} · {r.count} calls
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Main panel ─────────────────────────────────────────────────────────────

/**
 * AI Voice Agent analytics — self-contained panel meant to be dropped into
 * the main Engage dashboard. Fetches its own data and auto-refreshes every 30s.
 */
export default function AIVoiceAgentPanel() {
  const navigate = useNavigate();
  const { fetch: apiFetch } = useApiMethods();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const resp = await apiFetch(ENGAGE_AI_CALLER.DASHBOARD);
      setData(resp?.data || resp?.data?.data || {});
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load AI Voice Agent stats');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const stats = data?.stats || {};
  const outcomeDist = data?.outcome_distribution || [];

  return (
    <section className="space-y-3">
      {/* Section header */}
      <div className="flex items-center justify-between gap-3 border-t border-gray-200 dark:border-gray-700 pt-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white shadow-sm">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">AI Voice Agent</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Auto-calling analytics · updates every 30s</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <Link to="/engage/ai-caller/bucket" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700">
            <PhoneCall className="w-3.5 h-3.5" /> Manage bucket
          </Link>
        </div>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Calls today"   value={stats.total_calls_today} icon={Phone}       tone="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" />
        <StatCard label="Demos booked"  value={stats.demos_booked}      icon={Calendar}    tone="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400" />
        <StatCard label="Qualified"     value={stats.qualified_leads}   icon={Award}       tone="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400" />
        <StatCard label="Active now"    value={stats.active_calls}      icon={Activity}    tone="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400" />
        <StatCard
          label="Answer rate (7d)"
          value={`${stats.answer_rate_week ?? 0}%`}
          icon={PercentIcon}
          tone="bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400"
          sub={`${stats.week_stats?.answered ?? 0} of ${stats.week_stats?.total_calls ?? 0} calls`}
        />
        <StatCard
          label="Conversion (7d)"
          value={`${stats.conversion_rate_week ?? 0}%`}
          icon={TrendingUp}
          tone="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
          valueTone="text-emerald-600 dark:text-emerald-400"
          sub={`${stats.week_stats?.demos_booked ?? 0} demos booked`}
        />
      </div>

      {/* Volume trend | Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <VolumeTrend data={data?.volume_trend} />
        <Funnel funnel={data?.funnel} />
      </div>

      {/* Hourly | Avg duration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <HourlyDistribution data={data?.hourly_distribution} />
        <AvgDurationByOutcome rows={data?.avg_duration_by_outcome} />
      </div>

      {/* Source × outcome | Language conversion */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SourceMatrix rows={data?.source_outcome_matrix} />
        <LanguageConversion rows={data?.language_conversion} />
      </div>

      {/* Response speed + outcome mix */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-amber-500" /> Response speed
          </h3>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats.avg_response_minutes || 0}<span className="text-lg font-normal text-gray-500"> min</span>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            Avg minutes from enquiry received → first AI call ended (30d)
          </p>
        </div>

        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
            <BarChart2 className="w-4 h-4 text-indigo-500" /> Outcome mix · last 7 days
          </h3>
          {outcomeDist.length === 0
            ? <p className="text-sm text-gray-500 py-4 text-center dark:text-gray-400">No calls yet this week.</p>
            : <div className="space-y-1.5">
                {outcomeDist.map(o => {
                  const total = outcomeDist.reduce((s, x) => s + x.count, 0);
                  const pct = total ? Math.round(o.count / total * 100) : 0;
                  return (
                    <div key={o.outcome} className="flex items-center gap-2">
                      <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium min-w-[110px] text-center', OUTCOME_BADGE[o.outcome] || 'bg-gray-100 text-gray-700')}>
                        {prettyOutcome(o.outcome)}
                      </span>
                      <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300 w-14 text-right">{o.count} · {pct}%</span>
                    </div>
                  );
                })}
              </div>
          }
        </div>
      </div>

      {/* Recent calls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Recent AI calls</h3>
          <Link to="/engage/ai-caller/calls" className="text-xs text-teal-600 hover:text-teal-700 dark:text-teal-400 flex items-center gap-1">
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Enquiry</th>
              <th className="px-4 py-2 text-left font-medium">Mobile</th>
              <th className="px-4 py-2 text-left font-medium">Status</th>
              <th className="px-4 py-2 text-left font-medium">Outcome</th>
              <th className="px-4 py-2 text-right font-medium">Duration</th>
              <th className="px-4 py-2 text-left font-medium">Language</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</td></tr>
            : (data?.recent_calls || []).length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No calls yet.</td></tr>
            : (data?.recent_calls || []).map(c => (
              <tr key={c.id} onClick={() => navigate(`/engage/ai-caller/calls/${c.id}`)}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
                <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">{c.enquiry_name || '—'}</td>
                <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{c.enquiry_mobile || '—'}</td>
                <td className="px-4 py-2.5"><span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_BADGE[c.status] || 'bg-gray-100 text-gray-700')}>{prettyStatus(c.status)}</span></td>
                <td className="px-4 py-2.5">{c.outcome ? <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', OUTCOME_BADGE[c.outcome] || 'bg-gray-100 text-gray-700')}>{prettyOutcome(c.outcome)}</span> : '—'}</td>
                <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">{formatDuration(c.call_duration_seconds)}</td>
                <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{LANGUAGE_LABEL[c.detected_language] || c.detected_language || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
